import React from 'react';

/**
 * ApiErrorAlert: Readable inline error for tab previews and panels
 */
export const ApiErrorAlert = ({ error, onRetry, title }) => {
  if (!error) return null;

  const message = typeof error === "string" ? error : error.message;
  const reason = typeof error === "string" ? null : error.reason;
  const code = typeof error === "string" ? null : error.code;
  const status = typeof error === "string" ? null : error.status;
  const retryable = typeof error === "string" ? false : error.retryable;

  return (
    <div
      style={{
        background: "#fef2f2",
        border: "1px solid #fecaca",
        borderRadius: "10px",
        padding: "14px 16px",
        marginBottom: "16px",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
        <span style={{ fontSize: "18px", lineHeight: 1.2 }}>❌</span>
        <div style={{ flex: 1 }}>
          {title && (
            <div style={{ color: "#991b1b", fontSize: "12px", fontWeight: 700, marginBottom: "4px" }}>
              {title}
            </div>
          )}
          <div style={{ color: "#991b1b", fontSize: "14px", fontWeight: 600 }}>
            {message}
          </div>
          {reason && reason !== message && (
            <div style={{ color: "#b91c1c", fontSize: "13px", marginTop: "6px", lineHeight: 1.5 }}>
              {reason}
            </div>
          )}
          {(code || status) && (
            <div style={{ color: "#9f1239", fontSize: "11px", marginTop: "8px", opacity: 0.85 }}>
              {code && <span>Error code: {code}</span>}
              {code && status && <span> · </span>}
              {status && <span>HTTP {status}</span>}
            </div>
          )}
        </div>
        {retryable && onRetry && (
          <button
            onClick={onRetry}
            style={{
              padding: "6px 12px",
              borderRadius: "6px",
              border: "1px solid #fecaca",
              background: "#fff",
              color: "#991b1b",
              fontSize: "12px",
              fontWeight: 600,
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            Retry
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * ErrorBanner: Displays a dismissible warning/error banner
 * Used for non-fatal errors (partial data available)
 */
export const ErrorBanner = ({ error, onRetry, onDismiss }) => {
  if (!error) return null;

  const bgColor = error.severity === 'warning' ? '#f0f9ff' : '#fee2e2';
  const borderColor = error.severity === 'warning' ? '#bae6fd' : '#fecaca';
  const textColor = error.severity === 'warning' ? '#0369a1' : '#991b1b';
  const icon = error.severity === 'warning' ? '⚠️' : '❌';

  return (
    <div
      style={{
        background: bgColor,
        border: `1px solid ${borderColor}`,
        borderRadius: '8px',
        padding: '12px 16px',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
      }}
    >
      <span style={{ fontSize: '18px' }}>{icon}</span>
      <div style={{ flex: 1 }}>
        <div style={{ color: textColor, fontSize: '14px', fontWeight: 500 }}>
          {error.message}
        </div>
        {error.reason && error.reason !== error.message && (
          <div style={{ color: textColor, fontSize: '12px', opacity: 0.85, marginTop: '4px', lineHeight: 1.45 }}>
            {error.reason}
          </div>
        )}
        {error.endpoint && (
          <div style={{ color: textColor, fontSize: '12px', opacity: 0.7, marginTop: '4px' }}>
            Endpoint: <code style={{ background: 'rgba(0,0,0,0.05)', padding: '1px 4px', borderRadius: '3px' }}>
              /{error.endpoint}
            </code>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        {error.retryable && onRetry && (
          <button
            onClick={onRetry}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: `1px solid ${borderColor}`,
              background: 'rgba(255,255,255,0.5)',
              color: textColor,
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        )}
        {onDismiss && (
          <button
            onClick={onDismiss}
            style={{
              padding: '6px 8px',
              background: 'transparent',
              border: 'none',
              color: textColor,
              fontSize: '18px',
              cursor: 'pointer',
              opacity: 0.6,
            }}
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * FullErrorState: Displays when critical endpoints fail
 * Replaces entire Live Tab view with error message + retry button
 */
export const FullErrorState = ({ error, onRetry, loading }) => {
  if (!error) return null;

  return (
    <div
      style={{
        backgroundColor: '#fff',
        borderRadius: '10px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        padding: '40px 20px',
        textAlign: 'center',
        minHeight: '400px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div style={{ fontSize: '48px', marginBottom: '16px' }}>
        {error.status === 401 ? '🔐' : '❌'}
      </div>
      <h2 style={{ color: '#1f2937', marginBottom: '8px', fontSize: '20px' }}>
        {error.status === 401 ? 'Session Expired' : 'Unable to Load Live Data'}
      </h2>
      <p style={{ color: '#6b7280', marginBottom: '24px', fontSize: '14px', maxWidth: '400px' }}>
        {error.message}
      </p>
      {error.reason && error.reason !== error.message && (
        <p style={{ color: '#9ca3af', marginTop: '-16px', marginBottom: '24px', fontSize: '13px', maxWidth: '420px', lineHeight: 1.5 }}>
          {error.reason}
        </p>
      )}
      {error.status === 401 && (
        <button
          onClick={() => window.location.href = '/login'}
          disabled={loading}
          style={{
            padding: '10px 20px',
            borderRadius: '8px',
            border: 'none',
            background: '#1BA08E',
            color: '#fff',
            fontSize: '14px',
            fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? 'Redirecting...' : 'Go to Login'}
        </button>
      )}
      {error.retryable && error.status !== 401 && onRetry && (
        <button
          onClick={onRetry}
          disabled={loading}
          style={{
            padding: '10px 20px',
            borderRadius: '8px',
            border: 'none',
            background: '#1BA08E',
            color: '#fff',
            fontSize: '14px',
            fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? 'Refreshing...' : 'Retry'}
        </button>
      )}
      <div style={{ marginTop: '24px', fontSize: '12px', color: '#9ca3af' }}>
        If this persists, try refreshing the page or contacting support.
      </div>
    </div>
  );
};

/**
 * EndpointErrorLabel: Small label for forecast/mape panel
 */
export const EndpointErrorLabel = ({ error }) => {
  if (!error) return null;

  return (
    <div
      style={{
        background: '#fee2e2',
        border: '1px solid #fecaca',
        borderRadius: '8px',
        padding: '16px',
        textAlign: 'center',
        color: '#991b1b',
        fontSize: '14px',
      }}
    >
      <div style={{ marginBottom: '12px' }}>❌ {error.message}</div>
      {error.reason && error.reason !== error.message && (
        <div style={{ marginBottom: '12px', fontSize: '12px', opacity: 0.85 }}>{error.reason}</div>
      )}
      {error.retryable && (
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '6px 12px',
            borderRadius: '6px',
            border: '1px solid #fecaca',
            background: 'rgba(255,255,255,0.5)',
            color: '#991b1b',
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Retry
        </button>
      )}
    </div>
  );
};
